/**
 * Anda chat and feedback API.
 * Chat uses fetch + ReadableStream for SSE; feedback uses axios.
 */
import { createApiInstance } from './index';

const BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://127.0.0.1:8000/api/';

function getAuthHeaders() {
  const token = localStorage.getItem('token');
  return {
    'Content-Type': 'application/json',
    ...(token && { 'X-Auth-Token': token }),
  };
}

/**
 * Send chat message and stream response via SSE.
 * @param {Object} params
 * @param {number} params.project_id
 * @param {string} params.query_content
 * @param {number} [params.acct_no]
 * @param {string} [params.thread_id]
 * @param {string} [params.user_name]
 * @param {function} params.onEvent - (event) => void, event: { event, content?, text?, response_id?, message? }
 * @returns {Promise<string>} thread_id from X-Thread-Id header, or null
 */
export async function sendChatStream({ project_id, query_content, acct_no, thread_id, user_name, onEvent }) {
  const url = `${BASE_URL}anda/chat/`;
  const body = JSON.stringify({
    project_id,
    query_content,
    ...(acct_no != null && { acct_no }),
    ...(thread_id && { thread_id }),
    ...(user_name && { user_name }),
  });

  const response = await fetch(url, {
    method: 'POST',
    headers: getAuthHeaders(),
    body,
  });

  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    throw new Error(err.error || `Chat request failed: ${response.status}`);
  }

  const threadIdHeader = response.headers.get('X-Thread-Id');
  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';

  while (true) {
    const { done, value } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });
    const lines = buffer.split('\n');
    buffer = lines.pop() || '';
    for (const line of lines) {
      if (line.startsWith('data: ')) {
        try {
          const data = JSON.parse(line.slice(6));
          if (typeof onEvent === 'function') onEvent(data);
        } catch (_) {}
      }
    }
  }

  return threadIdHeader || null;
}

/**
 * Fetch chat history list for the user.
 * @param {Object} params
 * @param {number} params.acct_no
 * @param {number} params.project_id
 * @returns {Promise<Array<{ thread_id: string, chat_title: string, messages: Array }>>}
 */
export async function getChatHistory({ acct_no, project_id }) {
  const api = createApiInstance();
  const { data } = await api.get('anda/history/', {
    params: { acct_no, project_id },
  });
  return data;
}

/**
 * Submit thumbs up/down feedback for a message.
 * @param {Object} params
 * @param {string} params.thread_id
 * @param {number} params.acct_no
 * @param {string} params.message_id
 * @param {number} params.rating - 1 or -1
 * @param {string} [params.comment]
 */
export async function submitFeedback({ thread_id, acct_no, message_id, rating, comment }) {
  const api = createApiInstance();
  const { data } = await api.post('anda/feedback/', {
    thread_id,
    acct_no,
    message_id,
    rating,
    ...(comment && { comment }),
  });
  return data;
}

export async function deleteChatThread({ acct_no, project_id, thread_id }) {
  const api = createApiInstance();
  const { data } = await api.post('anda/history/delete/', {
    acct_no,
    project_id,
    thread_id,
  });
  return data;
}

export async function renameChatThread({ acct_no, project_id, thread_id, chat_title }) {
  const api = createApiInstance();
  const { data } = await api.post('anda/history/rename/', {
    acct_no,
    project_id,
    thread_id,
    chat_title,
  });
  return data;
}
